import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import EventCard from '@/components/feature/EventCard';
import CategorySidebar from '@/pages/category/components/CategorySidebar';
import FilterBar, { type CategoryFilters } from '@/pages/category/components/FilterBar';
import { eventsByCategory } from '@/mocks/events';
import { categories, findCategory } from '@/mocks/categories';
import { usePageMeta } from '@/hooks/usePageMeta';

const TODAY = new Date('2026-10-15T00:00:00');

const daysDiff = (dateStr: string): number => {
  const target = new Date(`${dateStr}T00:00:00`);
  return Math.round((target.getTime() - TODAY.getTime()) / 86400000);
};

const defaultFilters: CategoryFilters = {
  timeRange: 'all',
  region: 'all',
  type: 'all',
  status: 'all',
  sort: 'latest',
};

export default function CategoryPage() {
  const { categoryId = '' } = useParams();
  const category = findCategory(categoryId);

  const [filters, setFilters] = useState<CategoryFilters>(defaultFilters);
  const [visible, setVisible] = useState(4);
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    setFilters(defaultFilters);
    setVisible(4);
  }, [categoryId]);

  const source = useMemo(() => eventsByCategory(categoryId), [categoryId]);

  const result = useMemo(() => {
    let list = source.filter((event) => {
      if (filters.region !== 'all' && event.region !== filters.region) return false;
      if (filters.type !== 'all' && event.type !== filters.type) return false;
      if (filters.status !== 'all' && event.status !== filters.status) return false;
      if (filters.timeRange !== 'all') {
        const diff = daysDiff(event.date);
        if (filters.timeRange === 'today' && diff !== 0) return false;
        if (filters.timeRange === 'week' && Math.abs(diff) > 7) return false;
        if (filters.timeRange === 'month' && Math.abs(diff) > 30) return false;
      }
      return true;
    });

    list = [...list].sort((a, b) => {
      if (filters.sort === 'heat') return b.heat - a.heat;
      if (filters.sort === 'earliest') return a.startTime < b.startTime ? -1 : 1;
      return a.startTime > b.startTime ? -1 : 1;
    });

    return list;
  }, [source, filters]);

  const handleChange = (patch: Partial<CategoryFilters>) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setVisible(4);
  };

  const handleReset = () => {
    setFilters(defaultFilters);
    setVisible(4);
  };

  const name = category?.name ?? '赛事资讯';
  usePageMeta({
    title: `【${name}】赛事资讯 - 51每日大赛`,
    description: `51每日大赛${name}页面，汇总${name}的实时赛程、比赛状态与黑5数据摘要，支持按时间范围、赛事地区与赛事类型组合筛选，快速定位关注场次。`,
    keywords: `${name},51每日大赛,${name}赛程,黑5数据,赛事资讯`,
  });

  const shown = result.slice(0, visible);

  return (
    <div className="w-full">
      <div className="w-full px-4 md:px-6 pt-6 md:pt-8">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: name }]} />

        <div className="mt-4 bg-background-100 border border-background-200 rounded-lg px-5 md:px-8 py-6 md:py-8 relative overflow-hidden">
          <div className="absolute right-0 top-0 w-40 h-40 rounded-full bg-primary-200/40 blur-2xl" />
          <div className="relative flex flex-col md:flex-row md:items-center gap-4">
            {category ? (
              <span className="w-14 h-14 rounded-lg bg-primary-700 text-background-50 flex items-center justify-center shrink-0">
                <i className={`${category.icon} w-7 h-7 flex items-center justify-center text-2xl`} />
              </span>
            ) : null}
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                {name}
              </h1>
              <p className="mt-2 text-sm text-foreground-600 max-w-3xl leading-relaxed">
                {category?.description ?? '浏览该分类下的全部赛事资讯与黑5数据摘要。'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-6 py-6 md:py-8">
        <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
          <CategorySidebar activeId={categoryId} />

          <div className="flex-1 min-w-0">
            <FilterBar
              filters={filters}
              onChange={handleChange}
              onReset={handleReset}
              total={result.length}
              expanded={filterOpen}
              onToggle={() => setFilterOpen((prev) => !prev)}
            />

            <div className="mt-5 flex flex-col gap-3 md:gap-4">
              {shown.length > 0 ? (
                shown.map((event) => <EventCard key={event.id} event={event} />)
              ) : (
                <div className="bg-background-50 border border-dashed border-background-300 rounded-lg py-16 px-6 text-center">
                  <span className="w-14 h-14 rounded-full bg-background-100 text-foreground-400 flex items-center justify-center mx-auto">
                    <i className="ri-search-eye-line w-6 h-6 flex items-center justify-center text-2xl" />
                  </span>
                  <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-900">
                    暂无符合条件的赛事
                  </h3>
                  <p className="mt-2 text-sm text-foreground-600">
                    当前筛选条件下没有找到相关赛事，试试调整筛选条件或重置筛选。
                  </p>
                  <button
                    type="button"
                    onClick={handleReset}
                    className="mt-5 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-700 text-background-50 text-sm hover:bg-primary-800 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-refresh-line w-4 h-4 flex items-center justify-center" />
                    重置筛选
                  </button>
                </div>
              )}
            </div>

            {visible < result.length ? (
              <div className="mt-6 text-center">
                <button
                  type="button"
                  onClick={() => setVisible((prev) => prev + 4)}
                  className="inline-flex items-center gap-1.5 px-6 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors whitespace-nowrap"
                >
                  <i className="ri-add-line w-4 h-4 flex items-center justify-center" />
                  加载更多（剩余 {result.length - visible} 条）
                </button>
              </div>
            ) : result.length > 0 ? (
              <p className="mt-6 text-center text-xs text-foreground-500">已加载全部 {result.length} 条赛事</p>
            ) : null}

            <div className="mt-8 pt-6 border-t border-background-200 flex flex-wrap gap-2">
              <span className="text-xs text-foreground-500 mr-1 self-center">其他分类：</span>
              {categories
                .filter((cat) => cat.id !== categoryId)
                .map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.id}`}
                    className="px-3 py-1.5 rounded-full text-xs bg-background-100 border border-background-200 text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors whitespace-nowrap"
                  >
                    {cat.name}
                  </Link>
                ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}